import React from 'react'
import { bool, string } from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { CategoryType, PricingType, ThumbnailType } from '../common/PropTypes'
import { MonetizationButton } from '../MonetizationButton'
import { pushGtmData } from '@jelly/utils'

// Styled Components via Emotion
const WholeCardWrapper = styled.div`
	box-shadow: ${({ theme }) => theme.shadows.small};
	border: ${({ theme }) => theme.borders.default};
	border-top: 0.4rem solid ${({ theme }) => theme.reviews.border};
	margin: ${({ small }) => (small ? `0 0 1.25rem 0 ` : `1.25rem 0`)};
`
const FlexCardContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 10px;

	${({ theme }) => theme.MQ.L} {
		flex-direction: row;
	}
`

const LeftBox = styled.div`
	margin: 0 auto;

	${({ theme }) => theme.MQ.L} {
		margin-right: 32px;
	}
`

const RightBox = styled.div`
	padding: 0.5rem;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		margin: 0 auto 1rem;
		width: ${({ small }) => (small ? '235px' : '100%')};
	}
`

const IconWrapper = styled.div`
	vertical-align: top;
	text-align: center;
	width: 100%;
	img {
		padding: 1rem;
		margin: 1rem;
		margin-left: 0;
		width: 100%;
		box-shadow: ${({ theme }) => theme.shadows.large};
	}
`
let commonTitleStyles = ({ theme }) => css`
	color: ${theme.colors.darkGrey};
	margin: 0;
	padding-bottom: 5px;
	text-align: left;
`
const Title = styled.h3`
	${commonTitleStyles}
	font-size: 1.2rem;
`
const VerdictTitle = styled.h3`
	${commonTitleStyles}
`
const VerdictWrapper = styled.div`
	color: ${({ theme }) => theme.colors.darkGrey};
	width: 100%;
	border-top: 2px solid ${({ theme }) => theme.colors.lightGrey};
	border-bottom: 2px solid ${({ theme }) => theme.colors.lightGrey};
	padding: 15px;
	padding-left: 0;
	padding-bottom: 30px;
	margin-bottom: 20px;
	text-align: left;
`
const Text = styled.p`
	${({ theme }) => theme.text.body2};
	line-height: 1.5rem;
	margin: 0;
	padding: 0;
`
const BottomLink = styled.div`
	border-top: 2px solid ${({ theme }) => theme.colors.lightGrey};
	padding: 10px;
	text-align: right;
	width: 100%;
	a {
		${({ theme }) => theme.ctas.default};
		font-weight: 500;
	}
`

/**
 * Component for reviews card in reviews template,
 * right rail version use the small parameter
 */
export function CardReview({ bottomLink = false, linkTo, pricing, small = false, title, thumbnail, url, verdict }) {
	const icon = <img className="lazyload" data-src={thumbnail.path} alt={title} />
	const handleRfqButtonClick = () => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Compare Quotes Click',
			gaEventAction: title,
			gaEventLabel: 'Review Verdict Card',
			gaEventNoninteraction: false,
		})
	}
	const handleQtClick = (location = 'Button') => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Visit Site Click',
			gaEventAction: title,
			gaEventLabel: `Review Verdict Card ${location}`,
			gaEventNoninteraction: false,
		})
	}

	return (
		<WholeCardWrapper>
			<FlexCardContainer small={small}>
				{small ? null : (
					<LeftBox>
						<IconWrapper>{icon}</IconWrapper>
					</LeftBox>
				)}
				<RightBox small={small}>
					<Title>{title}</Title>
					<VerdictWrapper>
						<VerdictTitle>The Verdict</VerdictTitle>
						<Text>{verdict}</Text>
					</VerdictWrapper>
					<MonetizationButton
						{...{ pricing }}
						defaultMonetizationUrl={url}
						onRfqButtonClick={handleRfqButtonClick}
						onQtButtonClick={handleQtClick}
						serviceName={title}
						sectionType="bestpicks-review"
						templateType="category-review"
						type="cardReview"
						tabTitle="undefined"
					/>
					{/* need to include review in section and template bc this card is used on both templates */}
				</RightBox>
			</FlexCardContainer>
			{bottomLink && (
				<BottomLink>
					<a href={linkTo.url}>Full Review</a>
				</BottomLink>
			)}
		</WholeCardWrapper>
	)
}

CardReview.propTypes = {
	bottomLink: bool,
	linkTo: CategoryType.isRequired,
	pricing: PricingType.isRequired,
	small: bool,
	thumbnail: ThumbnailType.isRequired,
	title: string.isRequired,
	url: string.isRequired,
	verdict: string.isRequired,
}
